import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Input, Spinner, Table } from "reactstrap";
import { toast } from "react-toastify";
import AppModal from "../../../../Components/Common/AppModal";
import {
  envPreviewMasterSystemServer,
  envPushMasterSystemServer,
  envImportMasterSystemServer,
} from "../../../../helpers/fakebackend_helper";

// Preview and push the environment variable table to one server's DigitalOcean
// app (Change #100749). Secrets are typed here and never stored; a secret left
// blank stays exactly as it is on the app. Only keys the CRM pushed before can
// be removed; anything else live on the app is left alone.

const STATUS = {
  ADD: { label: "Add", cls: "bg-success-subtle text-success" },
  CHANGE: { label: "Change", cls: "bg-warning-subtle text-warning" },
  SAME: { label: "Same", cls: "bg-secondary-subtle text-secondary" },
  SECRET_KEPT: { label: "Secret kept", cls: "bg-secondary-subtle text-secondary" },
  NEEDS_VALUE: { label: "Needs value", cls: "bg-danger-subtle text-danger" },
  REMOVE: { label: "No longer wanted", cls: "bg-danger-subtle text-danger" },
  UNMANAGED: { label: "Not in table", cls: "bg-light text-muted border" },
};

const EnvPushModal = ({ open, onClose, servers, onImported, onApplied }) => {
  const [serverId, setServerId] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [secrets, setSecrets] = useState({});
  const [removeKeys, setRemoveKeys] = useState([]);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open && !serverId && servers?.length) setServerId(String(servers[0].Id));
  }, [open, servers, serverId]);

  const loadPreview = useCallback(async () => {
    if (!serverId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await envPreviewMasterSystemServer({ id: serverId });
      if (res?.status === 200) {
        setRows(Array.isArray(res.diff_list) ? res.diff_list : []);
      } else {
        setRows([]);
        setError(res?.message || "Could not load the preview");
      }
    } catch (e) {
      setRows([]);
      setError("Could not load the preview");
    }
    setLoading(false);
  }, [serverId]);

  useEffect(() => {
    if (!open) return;
    setSecrets({});
    setRemoveKeys([]);
    setResult(null);
    loadPreview();
  }, [open, loadPreview]);

  const typedSecrets = Object.fromEntries(
    Object.entries(secrets).filter(([, v]) => v !== "")
  );

  const pending = rows.filter((r) => {
    if (r.Status === "ADD" || r.Status === "CHANGE") return true;
    if (r.Status === "REMOVE") return removeKeys.includes(r.EnvKey);
    if (r.Status === "NEEDS_VALUE" || r.Status === "SECRET_KEPT") return !!typedSecrets[r.EnvKey];
    return false;
  }).length;

  const push = async () => {
    setBusy(true);
    try {
      const res = await envPushMasterSystemServer({
        Servers: [Number(serverId)],
        ...(Object.keys(typedSecrets).length ? { Secrets: typedSecrets } : {}),
        ...(removeKeys.length ? { RemoveKeys: removeKeys } : {}),
      });
      const r = (res?.results || [])[0] || null;
      setResult(r);
      if (res?.status === 200) {
        toast.success(r?.Message || res?.message || "Pushed");
        if ((r?.Changes || []).length && onApplied) onApplied([Number(serverId)]);
      } else {
        toast.error(r?.Message || res?.message || "Push failed");
      }
      setSecrets({});
      setRemoveKeys([]);
      await loadPreview();
    } catch (e) {
      toast.error("Push failed");
    }
    setBusy(false);
  };

  const importLive = async () => {
    setBusy(true);
    try {
      const res = await envImportMasterSystemServer({ id: serverId });
      if (res?.status === 200) {
        toast.success(res?.message || "Imported");
        if (onImported) onImported();
        await loadPreview();
      } else {
        toast.error(res?.message || "Import failed");
      }
    } catch (e) {
      toast.error("Import failed");
    }
    setBusy(false);
  };

  const unmanagedCount = rows.filter((r) => r.Status === "UNMANAGED").length;

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Push environment variables"
      icon="ri-upload-cloud-2-line"
      size="lg"
      loading={busy}
      loadingText="Talking to DigitalOcean..."
      onSubmit={push}
      submitLabel={pending ? `Push ${pending} change${pending === 1 ? "" : "s"}` : "Nothing to push"}
      submitDisabled={!pending || loading}
      cancelLabel="Close"
      secondaryAction={
        <Button
          color="light"
          className="border me-auto"
          disabled={busy || loading || !unmanagedCount}
          title="Copy this app's variables that are not in the table into it"
          onClick={importLive}
        >
          Import {unmanagedCount || ""} from this app
        </Button>
      }
    >
      <div className="d-flex align-items-center gap-2 mb-3">
        <Input
          type="select"
          bsSize="sm"
          style={{ maxWidth: 320 }}
          value={serverId}
          onChange={(e) => setServerId(e.target.value)}
        >
          {(servers || []).map((s) => (
            <option key={s.Id} value={s.Id}>
              {s.Name} {s.ServerType} ({s.Environment})
            </option>
          ))}
        </Input>
        <Button color="light" size="sm" className="border" disabled={loading} onClick={loadPreview}>
          <i className="ri-refresh-line" />
        </Button>
      </div>

      <Alert color="warning" className="py-2 small">
        Pushing rewrites the app spec and redeploys the app. Secrets left blank stay as they are.
      </Alert>

      {error && (
        <Alert color="danger" className="py-2 small">
          {error}
        </Alert>
      )}

      <div className="table-responsive">
        <Table size="sm" className="align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th>Key</th>
              <th>Status</th>
              <th>Live on app</th>
              <th>Will be</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="text-center text-muted py-3">
                  <Spinner size="sm" className="me-2" />
                  Comparing with DigitalOcean...
                </td>
              </tr>
            ) : rows.length ? (
              rows.map((r) => {
                const isSecret = r.Type === "SECRET";
                return (
                  <tr key={r.EnvKey} className={r.Status === "UNMANAGED" ? "text-muted" : ""}>
                    <td className="font-monospace small">
                      {r.EnvKey}
                      {r.Target && <div className="text-muted">from {r.Target}</div>}
                    </td>
                    <td>
                      <span className={`badge ${STATUS[r.Status]?.cls || ""}`}>
                        {STATUS[r.Status]?.label || r.Status}
                      </span>
                    </td>
                    <td className="font-monospace small text-truncate" style={{ maxWidth: 180 }}>
                      {r.LiveType === null ? (
                        <span className="text-muted">-</span>
                      ) : r.LiveType === "SECRET" ? (
                        <span className="text-muted">encrypted</span>
                      ) : (
                        r.LiveValue
                      )}
                      {r.LiveScope && r.Scope && r.LiveScope !== r.Scope && (
                        <div className="text-warning">scope {r.LiveScope}</div>
                      )}
                    </td>
                    <td className="font-monospace small" style={{ minWidth: 200 }}>
                      {r.Status === "REMOVE" ? (
                        <div className="form-check mb-0">
                          <Input
                            type="checkbox"
                            className="form-check-input"
                            id={`rm-${r.EnvKey}`}
                            checked={removeKeys.includes(r.EnvKey)}
                            onChange={() =>
                              setRemoveKeys((prev) =>
                                prev.includes(r.EnvKey)
                                  ? prev.filter((k) => k !== r.EnvKey)
                                  : [...prev, r.EnvKey]
                              )
                            }
                          />
                          <label className="form-check-label" htmlFor={`rm-${r.EnvKey}`}>
                            Remove from the app
                          </label>
                        </div>
                      ) : r.Status === "UNMANAGED" ? (
                        <span>left alone</span>
                      ) : isSecret ? (
                        <Input
                          bsSize="sm"
                          type="password"
                          autoComplete="new-password"
                          placeholder={
                            r.Status === "NEEDS_VALUE" ? "Secret value" : "New value (optional)"
                          }
                          value={secrets[r.EnvKey] || ""}
                          onChange={(e) => setSecrets({ ...secrets, [r.EnvKey]: e.target.value })}
                        />
                      ) : (
                        <span className="text-truncate d-inline-block" style={{ maxWidth: 220 }}>
                          {r.Value === "" ? <span className="text-muted">(empty)</span> : r.Value}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="4" className="text-center text-muted py-3">
                  Nothing in the table applies to this server and the app has no variables.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {result && (
        <div className={`small mt-3 ${Number(result.Success) ? "text-success" : "text-danger"}`}>
          {result.Message}
          {(result.Changes || []).map((c) => (
            <div key={c.EnvKey} className="text-muted">
              {c.Action} {c.EnvKey}
            </div>
          ))}
          {(result.Skipped || []).map((s) => (
            <div key={s.EnvKey} className="text-danger">
              {s.EnvKey}: {s.Message}
            </div>
          ))}
        </div>
      )}
    </AppModal>
  );
};

export default EnvPushModal;
